import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, User, LogOut, Menu, Package, LayoutDashboard, Truck, Sparkles, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { LOYALTY_SILVER_MIN, LOYALTY_GOLD_MIN } from "@/lib/loyalty";

export default function Header() {
  const { user, logout } = useAuth();
  const { totalItems, subtotal, loyalty } = useCart() || { totalItems: 0, subtotal: 0, loyalty: { tier: "none", percent: 0, amount: 0 } };
  const navigate = useNavigate();
  const location = useLocation();
  const onDashboard = ["/admin", "/delivery"].some(p => location.pathname.startsWith(p));

  const showLoyaltyBar = !onDashboard && (loyalty.tier === "elite" || totalItems > 0);

  // Two-segment progress: 0→₹250 (silver) and ₹250→₹1100 (gold), capped at 100%
  const capped = Math.min(subtotal, LOYALTY_GOLD_MIN);
  const loyaltyPct = Math.round((capped / LOYALTY_GOLD_MIN) * 100);
  const silverMarkerPct = Math.round((LOYALTY_SILVER_MIN / LOYALTY_GOLD_MIN) * 100); // ~23%

  let barLabel, barFillColor, barBorderColor, barBg;
  if (loyalty.tier === "elite") {
    barLabel = "Elite · 20% off";
    barFillColor = "#1A362B";
    barBorderColor = "border-[#1A362B]/50";
    barBg = "bg-[#1A362B]/10 hover:bg-[#1A362B]/15";
  } else if (loyalty.tier === "gold") {
    barLabel = "15% off unlocked";
    barFillColor = "#B8860B";
    barBorderColor = "border-[#B8860B]/50";
    barBg = "bg-[#B8860B]/10 hover:bg-[#B8860B]/15";
  } else if (loyalty.tier === "silver") {
    barLabel = `₹${(LOYALTY_GOLD_MIN - subtotal).toFixed(0)} to 15% off`;
    barFillColor = "#D96C4A";
    barBorderColor = "border-[#D96C4A]/40";
    barBg = "bg-[#D96C4A]/10 hover:bg-[#D96C4A]/15";
  } else {
    barLabel = `₹${(LOYALTY_SILVER_MIN - subtotal).toFixed(0)} to 10% off`;
    barFillColor = "#D96C4A";
    barBorderColor = "border-[#D96C4A]/40";
    barBg = "bg-[#D96C4A]/10 hover:bg-[#D96C4A]/15";
  }

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#EFE7D6]/80 border-b border-stone-200/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <div className="w-9 h-9 rounded-full bg-[#1A362B] flex items-center justify-center">
            <span className="font-serif-display text-[#EFE7D6] text-lg leading-none">श्री</span>
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-serif-display text-lg text-[#1A362B]">Shree Ram Pharma</span>
            <span className="label-caps text-[10px] text-stone-500">Est. 1998 · Since</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 label-caps text-stone-600">
          <Link to="/shop" className="hover:text-[#1A362B] transition-colors" data-testid="nav-shop">Shop</Link>
          {user && user.role === "customer" && (
            <Link to="/orders" className="hover:text-[#1A362B] transition-colors" data-testid="nav-orders">My Orders</Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" className="hover:text-[#1A362B] transition-colors" data-testid="nav-admin">Admin</Link>
          )}
          {user?.role === "delivery" && (
            <Link to="/delivery" className="hover:text-[#1A362B] transition-colors" data-testid="nav-delivery">Deliveries</Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {showLoyaltyBar && (
            <Link
              to="/cart"
              className={`hidden sm:flex items-center gap-2 pl-3 pr-3 py-1.5 rounded-full border transition-colors group ${barBorderColor} ${barBg}`}
              data-testid="loyalty-bar"
              aria-label={barLabel}
            >
              <div className="flex flex-col gap-1 min-w-[130px]">
                <span
                  className="label-caps text-[9px] leading-none whitespace-nowrap inline-flex items-center gap-1"
                  style={{ color: barFillColor }}
                  data-testid="loyalty-bar-label"
                >
                  {loyalty.tier === "elite" && <Sparkles className="w-3 h-3" />}
                  {barLabel}
                </span>
                <div className="relative h-1 w-full rounded-full bg-stone-200 overflow-hidden">
                  {loyalty.tier !== "elite" && (
                    <span
                      className="absolute top-1/2 -translate-y-1/2 w-0.5 h-2 bg-stone-400/70 z-10"
                      style={{ left: `${silverMarkerPct}%` }}
                      aria-hidden="true"
                    />
                  )}
                  <div
                    className="h-full rounded-full transition-[width] duration-500 ease-out"
                    style={{
                      width: `${loyalty.tier === "elite" ? 100 : loyaltyPct}%`,
                      backgroundColor: barFillColor,
                    }}
                    data-testid="loyalty-bar-fill"
                  />
                </div>
              </div>
            </Link>
          )}
          {!onDashboard && (
            <Link to="/cart" className="relative" data-testid="cart-link">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ShoppingBag className="w-5 h-5" />
              </Button>
              {totalItems > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1.5 bg-[#D96C4A] text-white border-0 text-[10px]" data-testid="cart-badge">
                  {totalItems}
                </Badge>
              )}
            </Link>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                <button className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-colors ${loyalty.tier === "elite" ? "border-[#B8860B]/70 bg-gradient-to-r from-[#FBEDC7]/60 to-[#FFF9E8]/60 hover:border-[#B8860B]" : "border-stone-200 hover:border-[#1A362B]"}`}>
                  <div className="relative">
                    <Avatar className={`w-7 h-7 ${loyalty.tier === "elite" ? "ring-2 ring-[#B8860B]/60 ring-offset-1 ring-offset-[#EFE7D6]" : ""}`}>
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-[#1A362B] text-[#EFE7D6] text-xs">
                        {user.name?.[0]?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    {loyalty.tier === "elite" && (
                      <span
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gradient-to-br from-[#F4C645] to-[#B8860B] flex items-center justify-center shadow-sm ring-1 ring-[#EFE7D6]"
                        data-testid="elite-crown-badge"
                        aria-label="Elite shopper"
                        title="Elite · 20% off on every order"
                      >
                        <Crown className="w-2.5 h-2.5 text-white" strokeWidth={2.5}/>
                      </span>
                    )}
                  </div>
                  <span className="text-sm hidden sm:block">{user.name?.split(" ")[0]}</span>
                  {loyalty.tier === "elite" && (
                    <span className="hidden md:inline label-caps text-[9px] text-[#8a6510] font-semibold tracking-wider">Elite</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm">{user.name}</span>
                    <span className="text-xs text-stone-500 font-normal">{user.email}</span>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <Badge variant="secondary" className="w-fit text-[10px] uppercase tracking-wider">{user.role}</Badge>
                      {loyalty.tier === "elite" && (
                        <Badge className="w-fit text-[10px] uppercase tracking-wider bg-gradient-to-r from-[#F4C645] to-[#B8860B] text-white border-0 inline-flex items-center gap-1" data-testid="elite-menu-badge">
                          <Crown className="w-3 h-3"/> Elite
                        </Badge>
                      )}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Console
                  </DropdownMenuItem>
                )}
                {user.role === "delivery" && (
                  <DropdownMenuItem onClick={() => navigate("/delivery")} data-testid="menu-delivery">
                    <Truck className="w-4 h-4 mr-2" /> Delivery Runs
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                  <Package className="w-4 h-4 mr-2" /> My Orders
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => navigate("/login")}
              className="rounded-full bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6] px-5"
              data-testid="signin-button"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
